import {useReducedMotion} from 'motion/react';
import * as motion from 'motion/react-m';

import {PILL_FADE_DURATION_MS, PILL_SLIDE_DURATION_MS} from './animation';

type FocusPillProps = {
  active: boolean;
  // Shared-layout id, unique per EventBarV2 instance (expanded vs compressed). All field pills
  // within one bar share it so Motion FLIPs the pill between them; keeping it distinct between
  // the expanded and compressed bars
  layoutId: string;
};

// The single traveling focus highlight: white fill + green border, rounded to a pill. It is
// rendered as an `absolute inset-0` child of whichever field cell is active; sharing one
// `layoutId` lets Motion FLIP it between cells (slide + resize) instead of popping. It honors
// reduced-motion (no slide/fade — instant reposition). Layout animations require the app-wide
// `domMax` LazyMotion features (loaded in _app.tsx); Storybook loads `domAnimation`, so the pill
// fades but does not slide there
const FocusPill: React.FC<FocusPillProps> = ({active, layoutId}) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      // Decorative — the input itself carries focus; the pill is only the visible indicator.
      aria-hidden
      // z-10 lifts the pill above the sibling cells' gray hover fill
      className="pointer-events-none absolute inset-0 z-10 rounded-full border-2 border-green-300 bg-white"
      data-testid="event-bar-focus-pill"
      layoutId={layoutId}
      initial={{opacity: 0}}
      animate={{opacity: active ? 1 : 0}}
      transition={{
        duration: shouldReduceMotion ? 0 : PILL_SLIDE_DURATION_MS / 1000,
        ease: 'easeInOut',
        opacity: {duration: shouldReduceMotion ? 0 : PILL_FADE_DURATION_MS / 1000},
      }}
    />
  );
};

export default FocusPill;
